/** Layout and camera arithmetic. Pure, DOM-free, and therefore testable.
 *
 * The camera lives here in *cell* space. Converting a cell to a pixel is
 * `packages/render/src/coords.ts`'s job and nobody else's (I14), which is why
 * this file never names a `PixelCoord`.
 */

export interface CameraView {
  /** Top-left visible cell. Fractional while a scroll is in flight. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Size of the viewport in cells. */
  readonly widthCells: number;
  readonly heightCells: number;
}

export interface MapExtent {
  readonly width: number;
  readonly height: number;
  readonly levels: number;
}

/**
 * Keep the camera inside the map. When the viewport is larger than the map the
 * camera is centred rather than pinned to 0, because a 36x36 map on a 4K screen
 * otherwise hugs the top-left corner with a band of nothing beside it.
 */
export function clampCamera(cam: CameraView, map: MapExtent): CameraView {
  const clampAxis = (v: number, view: number, extent: number): number => {
    if (view >= extent) return (extent - view) / 2;
    return Math.max(0, Math.min(extent - view, v));
  };
  const z = Math.max(0, Math.min(map.levels - 1, Math.trunc(cam.z)));
  return {
    x: clampAxis(cam.x, cam.widthCells, map.width),
    y: clampAxis(cam.y, cam.heightCells, map.height),
    z,
    widthCells: cam.widthCells,
    heightCells: cam.heightCells,
  };
}

/** Centre the camera on a cell, then clamp. Used by the minimap and by "centre on hero". */
export function centreCamera(cam: CameraView, map: MapExtent, x: number, y: number, z = cam.z): CameraView {
  return clampCamera(
    { ...cam, x: x - cam.widthCells / 2, y: y - cam.heightCells / 2, z },
    map,
  );
}

/** Scroll by whole cells. Arrow keys and edge-scroll both go through this. */
export function scrollCamera(cam: CameraView, map: MapExtent, dx: number, dy: number): CameraView {
  return clampCamera({ ...cam, x: cam.x + dx, y: cam.y + dy }, map);
}

/**
 * Viewport size in cells for a canvas of `pxW x pxH` at `tilePx` per cell.
 * Rounded *up*, so the renderer always has a cell of slack to cull against and
 * a partially visible row never pops in at the edge.
 */
export function viewportCells(pxW: number, pxH: number, tilePx: number): { widthCells: number; heightCells: number } {
  const t = tilePx > 0 ? tilePx : 1;
  return {
    widthCells: Math.max(1, Math.ceil(pxW / t)),
    heightCells: Math.max(1, Math.ceil(pxH / t)),
  };
}

// ------------------------------------------------------------------- minimap

export interface MinimapFit {
  /** Whole-pixel size of one map cell on the minimap. Never fractional. */
  readonly cellPx: number;
  /** Where the map image sits inside the box, in whole pixels. */
  readonly offsetX: number;
  readonly offsetY: number;
  readonly drawWidth: number;
  readonly drawHeight: number;
}

/**
 * Fit a `mapW x mapH` cell grid into a `boxW x boxH` pixel box at a whole number
 * of pixels per cell, centred. Whole pixels per cell is the entire trick: at
 * 2.7px per cell every third row of the minimap is a different thickness and the
 * coastline crawls when the window resizes.
 *
 * A map larger than the box in pixels still gets `cellPx = 1`; the caller is
 * expected to give the minimap a box at least as large as the map is wide, which
 * for a 144-cell map means 144px, comfortably inside the 236px sidebar.
 */
export function fitMinimap(mapW: number, mapH: number, boxW: number, boxH: number): MinimapFit {
  const safeW = Math.max(1, mapW);
  const safeH = Math.max(1, mapH);
  const cellPx = Math.max(1, Math.floor(Math.min(boxW / safeW, boxH / safeH)));
  const drawWidth = cellPx * safeW;
  const drawHeight = cellPx * safeH;
  return {
    cellPx,
    drawWidth,
    drawHeight,
    offsetX: Math.floor((boxW - drawWidth) / 2),
    offsetY: Math.floor((boxH - drawHeight) / 2),
  };
}

/**
 * Fit the grid into the box so it FILLS it, aspect preserved.
 *
 * `fitMinimap` quantises to a whole pixel per cell, which is right for a
 * standalone image and wrong for a panel: a 48-cell map in a 230px well lands
 * on 4px a cell, draws 192px, and leaves a 38px ring of dead slate between the
 * map's own border and the well that holds it - the "three concentric empty
 * rectangles" three reviewers filed against the same panel. The map's extent
 * has to BE the edge of the well.
 *
 * A fractional cell is not the problem the whole-pixel rule was written for:
 * the source canvas is still one texel per cell and the upscale is still
 * nearest-neighbour, so the only artefact is that some cells land 4px wide and
 * some 5px. At map scale that is invisible; a 38px dead margin is not.
 */
export function fillMinimap(mapW: number, mapH: number, boxW: number, boxH: number): MinimapFit {
  const safeW = Math.max(1, mapW);
  const safeH = Math.max(1, mapH);
  const cellPx = Math.max(0.0001, Math.min(boxW / safeW, boxH / safeH));
  const drawWidth = Math.round(cellPx * safeW);
  const drawHeight = Math.round(cellPx * safeH);
  return {
    cellPx,
    drawWidth,
    drawHeight,
    offsetX: Math.floor((boxW - drawWidth) / 2),
    offsetY: Math.floor((boxH - drawHeight) / 2),
  };
}

/** Minimap pixel -> map cell. Out-of-image clicks clamp instead of returning null. */
export function minimapCellAt(
  fit: MinimapFit, px: number, py: number, mapW: number, mapH: number,
): { x: number; y: number } {
  const cx = Math.floor((px - fit.offsetX) / fit.cellPx);
  const cy = Math.floor((py - fit.offsetY) / fit.cellPx);
  return {
    x: Math.max(0, Math.min(mapW - 1, cx)),
    y: Math.max(0, Math.min(mapH - 1, cy)),
  };
}

/** The camera rectangle in minimap pixels, for the bright outline. */
export function minimapCameraRect(
  fit: MinimapFit, cam: CameraView,
): { x: number; y: number; w: number; h: number } {
  return {
    x: fit.offsetX + Math.round(cam.x * fit.cellPx),
    y: fit.offsetY + Math.round(cam.y * fit.cellPx),
    w: Math.max(fit.cellPx, Math.round(cam.widthCells * fit.cellPx)),
    h: Math.max(fit.cellPx, Math.round(cam.heightCells * fit.cellPx)),
  };
}

// ------------------------------------------------------------------ zoom

/**
 * The magnifications the adventure map offers, in CSS pixels per world pixel,
 * ascending: three modes. 1 is the map as authored; three quarters and a half
 * are "see more of the map" - the two minifications the renderer offers (see
 * `ZOOM_OUT_STEPS` in `@game/render`'s `coords.ts`: the half is an exact box
 * filter, three quarters a soft blend). Nothing above 1: the map is authored
 * at one texel per pixel and a close look is not a mode a player asked for.
 */
export const MAP_ZOOM_LEVELS: readonly number[] = Object.freeze([0.5, 0.75, 1]);

/** The level the map opens at. */
export const DEFAULT_MAP_ZOOM = 1;

/**
 * The next level `dir` steps away from `zoom` (+1 in, -1 out), held to the
 * ends of `MAP_ZOOM_LEVELS`. A zoom that is not a level - a stale value, a
 * host that asked for 1.5 - snaps to the nearest one first, so a step is
 * always a step and never a correction.
 */
export function stepMapZoom(zoom: number, dir: 1 | -1): number {
  let at = 0;
  for (let i = 1; i < MAP_ZOOM_LEVELS.length; i++) {
    if (Math.abs((MAP_ZOOM_LEVELS[i] ?? 1) - zoom) < Math.abs((MAP_ZOOM_LEVELS[at] ?? 1) - zoom)) at = i;
  }
  const next = Math.max(0, Math.min(MAP_ZOOM_LEVELS.length - 1, at + dir));
  return MAP_ZOOM_LEVELS[next] ?? DEFAULT_MAP_ZOOM;
}
